// Field validation shared by the two new starter forms: the one a client fills
// in themselves, and the one the new starter completes from an emailed link.
//
// The server validates all of this again in routes/starterForm.js — this half
// exists so someone typing their own NI number finds out before they submit,
// not after. Keep the rules in step with the server's.

const NI_RE = /^[A-Za-z]{2}\d{6}[A-Da-d]$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

// People type a sort code as "12-34-56" or "12 34 56", and an account number
// with a space in the middle, because that is how their bank prints them. All
// of it is the right number, so accept the spacing and store the digits.
export function digitsOnly(value) {
  return String(value || '').replace(/[\s-]/g, '');
}

export function normaliseNi(value) {
  return String(value || '').replace(/\s/g, '').toUpperCase();
}

// What actually gets sent. The form keeps whatever they typed so the field
// doesn't rewrite itself under the cursor; this is applied on the way out.
export function normaliseStarterFields(form) {
  const out = { ...form };
  if (out.ni_number) out.ni_number = normaliseNi(out.ni_number);
  if (out.sort_code) out.sort_code = digitsOnly(out.sort_code);
  if (out.account_number) out.account_number = digitsOnly(out.account_number);
  if (out.postcode) out.postcode = out.postcode.trim().toUpperCase();
  return out;
}

export function validatePersonal(form, { selfService = false } = {}) {
  const errs = {};
  if (!form.first_name?.trim()) errs.first_name = 'Required';
  if (!form.last_name?.trim()) errs.last_name = 'Required';
  if (!form.email?.trim()) errs.email = selfService ? 'Required — this is where your payslips go' : 'Required for payslip delivery';
  else if (!EMAIL_RE.test(form.email)) errs.email = 'Invalid email format';
  if (!form.gender) errs.gender = 'Required';
  if (!form.date_of_birth) errs.date_of_birth = 'Required';

  if (!form.ni_number?.trim()) errs.ni_number = 'Required';
  else if (!NI_RE.test(normaliseNi(form.ni_number))) errs.ni_number = 'Format: AB123456C';

  return errs;
}

export function validateAddress(form) {
  const errs = {};
  if (!form.address_line_1?.trim()) errs.address_line_1 = 'Required';
  if (!form.city?.trim()) errs.city = 'Required';
  if (!form.postcode?.trim()) errs.postcode = 'Required';
  else if (!POSTCODE_RE.test(form.postcode.trim())) errs.postcode = 'Invalid UK postcode (e.g. SW1A 2AA)';
  return errs;
}

export function validateBank(form, { confirmAccountNumber = false } = {}) {
  const errs = {};
  if (!form.bank_account_name?.trim()) errs.bank_account_name = 'Required';

  if (!form.sort_code?.trim()) errs.sort_code = 'Required';
  else if (!/^\d{6}$/.test(digitsOnly(form.sort_code))) errs.sort_code = 'Must be 6 digits, e.g. 12-34-56';

  if (!form.account_number?.trim()) errs.account_number = 'Required';
  else if (!/^\d{8}$/.test(digitsOnly(form.account_number))) errs.account_number = 'Must be 8 digits';

  // Only the person typing it can catch a wrong-but-valid account number, so on
  // their own form they type it twice. Compared on the digits, so one of the two
  // carrying a space isn't reported as a mismatch.
  if (confirmAccountNumber) {
    if (!form.account_number_confirm?.trim()) errs.account_number_confirm = 'Please confirm your account number';
    else if (digitsOnly(form.account_number_confirm) !== digitsOnly(form.account_number)) {
      errs.account_number_confirm = "This doesn't match the account number above";
    }
  }

  return errs;
}

// Must match MAX_FILE_BYTES in backend/src/utils/fileValidation.js. Checked here
// so an oversized photo is caught on selection, rather than after the form is
// filled in and submitted.
export const MAX_UPLOAD_MB = 15;

export function validateFiles(files) {
  const tooBig = files.find(file => file.size > MAX_UPLOAD_MB * 1024 * 1024);
  if (tooBig) {
    return `${tooBig.name} is too big — the limit is ${MAX_UPLOAD_MB}MB per file. If it's a photo, try again at a lower resolution.`;
  }
  const wrongType = files.find(file => !/\.(pdf|jpe?g|png)$/i.test(file.name));
  if (wrongType) {
    return `${wrongType.name} isn't a type we can accept. Please attach a PDF, JPG or PNG.`;
  }
  return null;
}

// Human names for the summary an unsent form shows. Without these an error
// reads "Required" with nothing to say what is required.
export const FIELD_LABELS = {
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  gender: 'Gender',
  date_of_birth: 'Date of birth',
  ni_number: 'NI number',
  address_line_1: 'Address line 1',
  city: 'Town or city',
  postcode: 'Postcode',
  bank_account_name: 'Account name',
  sort_code: 'Sort code',
  account_number: 'Account number',
  account_number_confirm: 'Confirm account number',
  documents: 'P45 or starter checklist',
  start_date: 'Start date',
  annual_salary: 'Annual salary',
  hourly_rate: 'Hourly rate',
  contracted_hours_per_week: 'Contracted hours',
  contract_end_date: 'Contract end date',
  prorata_salary: 'Pro-rata salary',
  pt_days_per_week: 'Days per week',
  pt_hours_per_day: 'Hours per day',
  pt_weekly_hours_custom: 'Weekly hours',
  pt_fte_weekly_hours: 'Full-time weekly hours',
};

export function errorSummary(errs) {
  return Object.entries(errs)
    .filter(([, message]) => !!message)
    .map(([field, message]) => ({
      field,
      label: FIELD_LABELS[field] || field,
      message,
    }));
}

// The first section holding an error, given each section's field list.
//
// This matters more than it looks: a section that isn't the active one renders
// nothing at all, so an error inside it has nowhere to appear. Without moving
// the form to that section first, pressing the submit button does nothing
// visible and the form looks broken.
export function firstSectionWithError(errs, sectionFields) {
  for (let i = 0; i < sectionFields.length; i += 1) {
    if (sectionFields[i].some(field => errs[field])) return i;
  }
  return null;
}

// Scroll to whatever the first flagged field is. Both forms are long enough
// that an error above the fold is otherwise invisible. Runs after the section
// switch has rendered, so the field it is looking for exists.
export function scrollToFirstError() {
  setTimeout(() => {
    const firstErrorField = document.querySelector('[class*="border-red"]');
    if (firstErrorField) firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 60);
}
